class Pool_DataManager {
    constructor() {
        this.apiUrl = window.POOL_CONFIG.apiUrl;
        this.accessToken = window.POOL_CONFIG.accessToken;
    }

    /**
     * Fetches product data from Shopify GraphQL API.
     * @returns {Promise<Object>} - Processed product data categorized by type.
     */
    async fetchProductData() {
        const query = `
        {
            products(first: 100, query: "tag:pool_configurator") {
                edges {
                    node {
                        id
                        title
                        tags
                        images(first: 1) { edges { node { src } } }
                        options { name values }
                        wallHeightMetafield: metafield(namespace: "custom", key: "wall_height") { value }
                        materialMetafield: metafield(namespace: "custom", key: "material") { value }
                        compatibleLinerTypes: metafield(namespace: "custom", key: "compatible_liner_types") { value }
                        variants(first: 50) {
                            edges {
                                node {
                                    id
                                    price { amount currencyCode }
                                    availableForSale
                                    sku
                                    selectedOptions { name value }
                                    variantWallHeightMetafield: metafield(namespace: "custom", key: "wall_height") { value }
                                    variantMaterialMetafield: metafield(namespace: "custom", key: "material") { value }
                                }
                            }
                        }
                    }
                }
            }
        }`;

        try {
            const response = await fetch(this.apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Shopify-Storefront-Access-Token": this.accessToken,
                },
                body: JSON.stringify({ query }),
            });

            const json = await response.json();
            return this.processProductData(json.data.products.edges.map(edge => edge.node));
        } catch (error) {
            console.error("Error fetching product data:", error);
            return { pools: [], liners: [], skimmer: [], filters: [], pumps: [] };
        }
    }

    /**
     * Processes raw product data, categorizing it into pools, liners, filters, pumps, and skimmers.
     * @param {Array} products - Raw product data from Shopify.
     * @returns {Object} - Categorized product data.
     */
    processProductData(products) {
        return {
            pools: this.getProductsByTag(products, "Pool"),
            liners: this.getProductsByTag(products, "Liner"),
            skimmer: this.getProductsByTag(products, "Skimmer"),
            filters: this.getProductsByTag(products, "Filter"),
            pumps: this.getProductsByTag(products, "Pump"),
        };
    }

    /**
     * Filters products by a specific tag and maps their data.
     * @param {Array} products - Raw product data.
     * @param {string} tag - Product category tag (e.g., "Pool", "Liner").
     * @returns {Array} - Mapped product data for the specified category.
     */
    getProductsByTag(products, tag) {
        return products
            .filter(product => product.tags.includes(tag))
            .map(product => this.mapProductData(product));
    }

    /**
     * Maps raw product data to a structured format.
     * @param {Object} product - Raw product object from Shopify.
     * @returns {Object} - Mapped product data.
     */
    mapProductData(product) {
        const isPool = product.tags.includes("Pool");
        const isLiner = product.tags.includes("Liner");
        const isSkimmer = product.tags.includes("Skimmer");

        return {
            name: product.title,
            image: product.images.edges[0]?.node.src || null,
            stock: product.variants.edges.some(v => v.node.availableForSale) ? 1 : 0,
            isDisabled: product.variants.edges.length === 0,
            ...(isPool && this.extractPoolMetadata(product)),
            ...(isSkimmer && this.extractSkimmerData(product)),
            variants: this.mapProductVariants(product.variants.edges, isLiner),
        };
    }

    /**
     * Extracts pool-specific metadata.
     * @param {Object} product - Pool product data.
     * @returns {Object} - Extracted metadata (height, material, liner compatibility).
     */
    extractPoolMetadata(product) {
        return {
            height: product.wallHeightMetafield?.value || null,
            material: product.materialMetafield?.value || null,
            compatibleLinerTypes: product.compatibleLinerTypes?.value || null,
        };
    }

    /**
     * Extracts skimmer-specific metadata.
     * @param {Object} product - Skimmer product data.
     * @returns {Object} - Extracted metadata (price, availability, ID).
     */
    extractSkimmerData(product) {
        const variant = product.variants.edges[0]?.node;
        return variant
            ? {
                price: variant.price.amount,
                available: variant.availableForSale,
                id: variant.id,
            }
            : {};
    }

    /**
     * Maps product variants to a structured format.
     * @param {Array} variants - Product variant data from Shopify.
     * @param {boolean} isLiner - Flag indicating if the product is a liner.
     * @returns {Array} - Mapped variant data.
     */
    mapProductVariants(variants, isLiner) {
        return variants.map(({ node }) => ({
            id: node.id,
            price: node.price.amount,
            available: node.availableForSale,
            size: this.getSelectedOption(node.selectedOptions, "size"),
            shape: this.getSelectedOption(node.selectedOptions, "shape"),
            ...(isLiner && {
                height: this.getSelectedOption(node.selectedOptions, "height"),
                type: this.getSelectedOption(node.selectedOptions, "type"),
            }),
        }));
    }

    /**
     * Extracts the value of a specific selected option from a variant.
     * @param {Array} options - Selected options array.
     * @param {string} name - Option name (e.g., "size", "shape").
     * @returns {string|null} - Extracted option value or null if not found.
     */
    getSelectedOption(options, name) {
        return options.find(option => option.name.toLowerCase() === name)?.value || null;
    }
}

export { Pool_DataManager };
